import { useEffect, useRef, useState } from "react";
import { MessageCircleQuestion, Send, X, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import { useLang } from "../lib/i18n";

const sessionId = () => {
  let s = localStorage.getItem("tl_help_session");
  if (!s) { s = crypto.randomUUID ? crypto.randomUUID() : String(Date.now()); localStorage.setItem("tl_help_session", s); }
  return s;
};

async function stream(text, onChunk) {
  const res = await fetch(`${API}/help/chat`, { method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId(), message: text }) });
  if (!res.ok) throw new Error("Assistant indisponible");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const d = p.replace(/^data: /, "");
      if (d === "[DONE]") return;
      const j = JSON.parse(d);
      if (j.error) throw new Error(j.error);
      onChunk(j.t);
    }
  }
}

export function HelpChat() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);
  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    const add = (c) => setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: m[m.length - 1].text + c }]);
    try { await stream(q, add); } catch (e) { add(e.message); }
    setBusy(false);
  };
  return (
    <>
      <button onClick={() => setOpen(!open)} className="fixed right-4 bottom-24 lg:bottom-6 z-40 h-14 w-14 rounded-full bg-amber-600 text-white shadow-lg grid place-items-center hover:bg-amber-700 transition-colors" aria-label={t("help.title")} data-testid="help-chat-toggle">
        {open ? <X /> : <MessageCircleQuestion />}
      </button>
      {open && (
        <div className="fixed right-4 bottom-40 lg:bottom-24 z-40 w-[calc(100vw-2rem)] max-w-sm h-[60vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col tl-fade" data-testid="help-chat-panel">
          <div className="tl-stripe text-white px-4 py-3 rounded-t-2xl font-bold">{t("help.title")}</div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 text-sm">
            {!msgs.length && <p className="text-slate-500">{t("help.intro")}</p>}
            {msgs.map((m, i) => (
              <div key={i} className={`rounded-xl px-3 py-2 whitespace-pre-wrap ${m.role === "user" ? "bg-amber-100 ml-8" : "bg-slate-100 mr-8"}`} data-testid={`help-msg-${i}`}>
                {m.text || <Loader2 size={14} className="animate-spin" />}
              </div>
            ))}
            <div ref={end} />
          </div>
          <div className="p-2 border-t flex gap-2">
            <input className="tl-input flex-1" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("help.placeholder")} data-testid="help-chat-input" />
            <button className="tl-btn tl-btn-primary" onClick={send} disabled={busy} data-testid="help-chat-send"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
}
